import React, { type ComponentProps, memo } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, { DerivedValue, SharedValue, useAnimatedStyle } from 'react-native-reanimated';

import { Image } from '@sg/garnish';

import { Hexagon, Logo } from '../../assets';

// ─────────────────────────────────────────────────────────────────────────────

export const PermissionsPromptHexagon = memo((props: Props) => {
  const { scaleSV, opacitySV, rotateSV, entering, exiting, logoEntering, hideLogo = false } = props;

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerAnimatedStyles = useAnimatedStyle(() => ({
    transform: rotateSV
      ? [{ scale: scaleSV.value }, { rotate: `${rotateSV.value}deg` }]
      : [{ scale: scaleSV.value }],
  }));

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Animated.View style={containerAnimatedStyles} entering={entering} exiting={exiting}>
      <View>
        <Image source={Hexagon} style={styles.image} contentFit="contain" accessible={false} />
      </View>

      {!hideLogo ? <LogoOverlay opacitySV={opacitySV} entering={logoEntering} /> : null}
    </Animated.View>
  );
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

const LogoOverlay = memo((params: LogoOverlayParams) => {
  const { opacitySV, entering } = params;

  const overlayAnimatedStyles = useAnimatedStyle(() =>
    opacitySV ? { opacity: opacitySV.value } : {},
  );

  const overlayStyles = [styles.overlay, overlayAnimatedStyles];

  return (
    <Animated.View style={overlayStyles} entering={entering}>
      <Image source={Logo} style={styles.image} contentFit="contain" accessible={false} />
    </Animated.View>
  );
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  overlay: {
    position: 'absolute',
  },
  image: {
    width: 1000,
    height: 1000,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  scaleSV: SharedValue<number> | DerivedValue<number>;
  opacitySV?: SharedValue<number>;
  rotateSV?: SharedValue<number>;
  entering?: ComponentProps<typeof Animated.View>['entering'];
  exiting?: ComponentProps<typeof Animated.View>['exiting'];
  logoEntering?: ComponentProps<typeof Animated.View>['entering'];
  hideLogo?: boolean;
};

type LogoOverlayParams = {
  opacitySV?: SharedValue<number>;
  entering?: ComponentProps<typeof Animated.View>['entering'];
};
